/* ============================================================================
   CRONÓMETRO DE VUELO — tiempo de arranque a apagado, con tramos en tierra.

   CÓMO SE MIDE
   ------------
   El vuelo es una sucesión de tramos que alternan:

     ARRANQUE → tierra → despegue → aire → aterrizaje → tierra → … → APAGADO

   De ahí salen dos números:

     FT   TIEMPO TOTAL, DEL ARRANQUE DEL MOTOR AL APAGADO. Es lo que mide un
          Hobbs: corre mientras el motor esté en marcha, esté la aeronave en el
          aire o esperando en tierra.

          **Va al libro del piloto y a su duty time.** Las esperas con rotores
          girando SON tiempo de servicio y cuentan para sus límites.

     AT   AIR TIME. Estrictamente los tramos en que la aeronave no toca la
          superficie. Es FT menos las esperas en tierra.

          **Va al programa y a la bitácora de mantenimiento.** Es lo que
          consume vida de componentes.

   En un vuelo de trabajo con muchas tomas y despegues cortos la diferencia es
   grande, y por eso hay que medir los dos: usar el FT para mantenimiento
   adelanta las inspecciones, y usar el AT para el piloto le descuenta horas de
   servicio que sí voló.

   El nombre «block time» quedó descartado: en la convención aeronáutica es de
   calzo a calzo, que se parece más a FT que a AT, y usarlo para el tiempo en
   el aire invertía el significado. Air Time no admite esa confusión.

   POR QUÉ SE GUARDAN INSTANTES Y NO CONTADORES
   --------------------------------------------
   Cada tramo guarda su instante de inicio y de fin en milisegundos absolutos.
   Un contador que se incrementa con un temporizador se congela cuando el
   navegador suspende la pestaña —cosa que iOS hace apenas la app pasa a
   segundo plano o se apaga la pantalla—, y el vuelo terminaría con menos
   tiempo del real. Con instantes, el reloj se recalcula al volver y no se
   pierde nada.
   ========================================================================== */

export const TIERRA = "tierra";
export const AIRE = "aire";

/** Un cronómetro recién creado, sin arrancar. */
export function cronometroVacio() {
  return { arranqueMs: null, apagadoMs: null, tramos: [] };
}

const esMs = (v) => Number.isFinite(v) && v > 0;

/**
 * Arranca el motor. Empieza el primer tramo en TIERRA.
 *
 * Arrancar dos veces no reinicia: devolvería a cero un vuelo en curso.
 */
export function arrancar(estado, ahoraMs = Date.now()) {
  if (!esMs(ahoraMs)) return { estado, error: "Instante inválido." };
  if (estado.arranqueMs !== null) {
    return { estado, error: "El motor ya está arrancado. Para reiniciar hay que cerrar este vuelo." };
  }
  return {
    estado: { arranqueMs: ahoraMs, apagadoMs: null, tramos: [{ tipo: TIERRA, desdeMs: ahoraMs, hastaMs: null }] },
    error: null,
  };
}

/**
 * Cambia de tierra a aire o de aire a tierra.
 *
 * Un cambio hacia atrás en el tiempo se rechaza en vez de producir un tramo de
 * duración negativa. El reloj del dispositivo puede saltar: al sincronizar por
 * red, al cruzar husos, o si alguien lo corrige a mano en pleno vuelo.
 */
export function alternar(estado, ahoraMs = Date.now()) {
  if (estado.arranqueMs === null) return { estado, error: "Todavía no se arrancó el motor." };
  if (estado.apagadoMs !== null) return { estado, error: "Este vuelo ya está cerrado." };
  if (!esMs(ahoraMs)) return { estado, error: "Instante inválido." };

  const abierto = estado.tramos[estado.tramos.length - 1];
  if (!abierto || abierto.hastaMs !== null) return { estado, error: "No hay un tramo abierto." };
  if (ahoraMs < abierto.desdeMs) {
    return { estado, error: "El reloj del dispositivo retrocedió: no se puede cerrar un tramo antes de que empiece." };
  }

  const siguiente = abierto.tipo === TIERRA ? AIRE : TIERRA;
  return {
    estado: {
      ...estado,
      tramos: [
        ...estado.tramos.slice(0, -1),
        { ...abierto, hastaMs: ahoraMs },
        { tipo: siguiente, desdeMs: ahoraMs, hastaMs: null },
      ],
    },
    error: null,
  };
}

/**
 * Apaga el motor y cierra el vuelo.
 *
 * Si el último tramo estaba en AIRE, el vuelo se cierra en el aire: es un dato
 * incoherente —no se apaga el motor volando— y se marca, en vez de inventar un
 * aterrizaje que nadie registró.
 */
export function apagar(estado, ahoraMs = Date.now()) {
  if (estado.arranqueMs === null) return { estado, error: "Todavía no se arrancó el motor." };
  if (estado.apagadoMs !== null) return { estado, error: "Este vuelo ya está cerrado." };
  if (!esMs(ahoraMs)) return { estado, error: "Instante inválido." };

  const abierto = estado.tramos[estado.tramos.length - 1];
  if (abierto && ahoraMs < abierto.desdeMs) {
    return { estado, error: "El reloj del dispositivo retrocedió: no se puede apagar antes del último cambio." };
  }

  return {
    estado: {
      ...estado,
      apagadoMs: ahoraMs,
      tramos: estado.tramos.map((t, i) => (i === estado.tramos.length - 1 ? { ...t, hastaMs: ahoraMs } : t)),
    },
    error: null,
  };
}

/** Milisegundos a horas decimales, redondeadas a la centésima. */
export function aHorasDecimales(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.round((ms / 3600000) * 100) / 100;
}

/** Milisegundos a "h:mm", como se escribe en la bitácora de papel. */
export function aHorasMinutos(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const totalMin = Math.round(ms / 60000);
  return `${Math.floor(totalMin / 60)}:${String(totalMin % 60).padStart(2, "0")}`;
}

/**
 * Resultado del cronómetro.
 *
 * `ahoraMs` permite calcular con el tramo abierto: mientras el vuelo está en
 * curso, el tramo actual se mide hasta este instante.
 */
export function resumen(estado, ahoraMs = Date.now()) {
  const vacio = {
    enCurso: false, cerrado: false, tipoActual: null,
    ftMs: 0, atMs: 0, tierraMs: 0,
    ft: null, at: null, tierra: null,
    ftHhMm: null, atHhMm: null, tierraHhMm: null,
    aterrizajes: 0, tramos: [], avisos: [],
  };

  if (estado?.arranqueMs === null || estado?.arranqueMs === undefined) return vacio;

  const cerrado = estado.apagadoMs !== null;
  const fin = cerrado ? estado.apagadoMs : ahoraMs;
  const avisos = [];

  if (!cerrado && fin < estado.arranqueMs) {
    avisos.push("El reloj del dispositivo está por detrás del arranque: los tiempos no son fiables.");
  }

  // Un estado a medio escribir —recuperado de una escritura interrumpida en
  // IndexedDB, por ejemplo— puede traer el arranque sin los tramos.
  const listaTramos = Array.isArray(estado.tramos) ? estado.tramos : [];
  const tramos = listaTramos.map((t) => {
    const hasta = t.hastaMs ?? fin;
    const ms = Math.max(0, hasta - t.desdeMs);
    return { ...t, hastaMs: t.hastaMs, duracionMs: ms, abierto: t.hastaMs === null };
  });

  const ftMs = Math.max(0, fin - estado.arranqueMs);
  const tierraMs = tramos.filter((t) => t.tipo === TIERRA).reduce((s, t) => s + t.duracionMs, 0);
  const atMs = Math.max(0, ftMs - tierraMs);

  /**
   * Un aterrizaje es un tramo de AIRE seguido de uno en TIERRA.
   *
   * No alcanza con que el tramo esté cerrado: al apagar el motor se cierra el
   * último tramo sea cual sea, y si ese tramo era de aire se contaba un
   * aterrizaje que nadie registró. Justo lo contrario de lo que se busca: el
   * aviso de "se cerró volando" existe para que alguien lo corrija, no para
   * que la app lo tape sumando un aterrizaje inventado.
   */
  const aterrizajes = tramos.reduce(
    (n, t, i) => (t.tipo === AIRE && tramos[i + 1]?.tipo === TIERRA ? n + 1 : n),
    0,
  );

  const ultimo = tramos[tramos.length - 1];
  if (cerrado && ultimo?.tipo === AIRE) {
    avisos.push("El vuelo se cerró con el último tramo en el aire: no se apaga el motor volando. Revisa si falta registrar el aterrizaje.");
  }
  if (cerrado && aterrizajes === 0 && atMs > 0) {
    avisos.push("Hay tiempo en el aire pero ningún aterrizaje registrado.");
  }

  /**
   * Horas del reloj de cada hito, para la planilla.
   *
   * Se guardan como instantes ISO, no como texto ya formateado: el formato
   * depende del huso y de si el operador escribe en local o en UTC, y eso se
   * decide al mostrar. Guardar "18:40" pierde el día y el huso.
   */
  // `new Date("x").toISOString()` lanza en vez de devolver null. Con un estado
  // a medio escribir —recuperado de una escritura interrumpida— eso tiraba
  // abajo todo el resumen, y con él la pantalla del cronómetro.
  const aIso = (ms) => (esMs(ms) ? new Date(ms).toISOString() : null);
  const horaArranque = aIso(estado.arranqueMs);
  const horaApagado = cerrado ? aIso(estado.apagadoMs) : null;
  // Un aterrizaje es el instante en que termina un tramo de AIRE seguido de
  // uno en TIERRA: los mismos que se cuentan arriba, con su hora.
  const horasAterrizaje = tramos
    .filter((t, i) => t.tipo === AIRE && tramos[i + 1]?.tipo === TIERRA)
    .map((t) => aIso(t.hastaMs)).filter(Boolean);
  const horasDespegue = tramos
    .filter((t) => t.tipo === AIRE)
    .map((t) => aIso(t.desdeMs)).filter(Boolean);

  return {
    enCurso: !cerrado,
    cerrado,
    horaArranque,
    horaApagado,
    horasDespegue,
    horasAterrizaje,
    tipoActual: ultimo?.abierto ? ultimo.tipo : null,
    ftMs, atMs, tierraMs,
    ft: aHorasDecimales(ftMs),
    at: aHorasDecimales(atMs),
    tierra: aHorasDecimales(tierraMs),
    ftHhMm: aHorasMinutos(ftMs),
    atHhMm: aHorasMinutos(atMs),
    tierraHhMm: aHorasMinutos(tierraMs),
    aterrizajes,
    tramos,
    avisos,
  };
}
